import type { CalendarEntry } from "../catalog.js";
import { localWhen } from "../pages/fixtures-heading.js";
import { formatClock } from "../ui/dom.js";

import type { StreamStatus } from "./stream.js";
import type { LiveGame } from "./types.js";

/**
 * What the round page says about itself — derived, never guessed.
 *
 * A round that has not started is the case this exists for: the distributor
 * accepts `Subscribe` for ANY round id and holds the stream open with zero
 * frames until the round begins (relay-distributor `state.rs:307-341`,
 * `broadcast_service.rs:406-451`), so the stream can never distinguish "not
 * started" from "silent". The calendar can: `ListBroadcasts.upcoming` carries
 * `starts_at_ms`. Hence upcoming-ness comes from the calendar and liveness from
 * the stream, exactly as the app does it (BACKEND-RPCS §1.4).
 *
 * Copy is the app's, verbatim: the four board advisories are
 * `LiveBoardV2Screen.kt:201-204` shown ONE at a time by
 * `LiveBoardV2Presentation.statusNote()`'s precedence (roundFinished >
 * streamLost > reconnecting > stale), and the empty-round line is
 * `TournamentScreen.kt:533`. Pure: no DOM, no clock, no fetch.
 */

/** The four board advisories, app copy verbatim (APP-CUJ-DESIGN §5). */
export const NOTICE_ROUND_FINISHED = "Round finished";
export const NOTICE_STREAM_LOST = "Live updates lost — showing last position";
export const NOTICE_RECONNECTING = "Reconnecting…";
export const NOTICE_STALE = "Data may be delayed";
/** `TournamentScreen.kt:533` — the round is on, the boards are not there. */
export const NOTICE_NO_BOARDS = "Boards for this round aren’t available yet.";

/** `TournamentView.swift:951` / `TournamentScreen.kt:703` — the app's start fallback. */
export const START_SOON = "Starts soon";
/**
 * The schedule column prints "TBD" for `startsAfterPrevious` (`ScheduleMapper.kt:73`);
 * a page with room for a sentence says the sentence.
 */
export const START_AFTER_PREVIOUS = "Starts after the previous round";
/** The neutral round name — KMP `LiveRoundRouter.roundName():52`. */
export const TITLE_NEUTRAL = "Live";

/** KMP `HomeSchedulePresentation.kt:90` `SOON_WINDOW_MS`: 3 h, not Android's 2 h. */
export const SOON_WINDOW_MS = 3 * 3_600_000;

export type RoundPill = "upcoming" | "connecting" | "live" | "reconnecting" | "finished" | "offline";

/**
 * Where a round's content comes from — the web mirror of KMP
 * `RoundRenderSource` (`shared/src/commonMain/kotlin/com/chessstreamlive/viewmodel/catalog/CatalogProjections.kt:8-41`).
 *
 * `results-pending` is the arm that matters. A round that is over but whose
 * games are not in the catalog yet is a TERMINAL EMPTY STATE, never the live
 * plane: routing it to live is #727's phantom subscription, where a finished
 * round opens and retains a distributor stream forever. There is deliberately
 * no `LIVE_SNAPSHOT` arm (PR #2177 review R5) — the honest answer while the
 * publish job catches up is that the results are pending, and the page revisits
 * it once rather than polling.
 */
export type RoundRenderSource = "live" | "served" | "upcoming" | "results-pending";

/** What the hub says over a finished round the catalog has not published yet. */
export const RESULTS_PENDING_NOTE = "Results coming shortly";

/**
 * The projection itself, argument-for-argument the Kotlin one.
 *
 * The web's input is the `ListBroadcasts` BUCKET rather than
 * `BroadcastLiveState` — the calendar has no state field ("state is the
 * BroadcastList bucket", `catalog.proto:642`) — but the buckets are the same
 * three facts: `active` is Kotlin's STREAMING, `finished` is FINISHED, and
 * `upcoming` covers SCHEDULED. The unspecified-and-published arm has no web
 * counterpart because a bucket is never unspecified.
 */
export function roundRenderSource(state: CalendarEntry["state"], isPublished: boolean): RoundRenderSource {
  switch (state) {
    case "active":
      return "live";
    case "finished":
      return isPublished ? "served" : "results-pending";
    default:
      return "upcoming";
  }
}

/**
 * The same projection with publication read off the calendar entry.
 *
 * `edition_id` is NOT the publication signal, though its proto comment
 * ("present once archived") reads like one. `edition_id_of()` in
 * `relay-control/src/calendar.rs` calls `mint_edition_id` unconditionally for
 * every discovered round and there is no `None` path, so the id is set from
 * first discovery — for upcoming rounds too. Keying on it made the
 * `results-pending` arm unreachable and labelled every finished round "served"
 * whether or not anything had been published.
 *
 * `isPublished` is the server's own probe answer instead — see `CalendarEntry`.
 *
 * NOTE (#2176): the WEB no longer ROUTES a finished round on this projection.
 * `pages/round-route.ts` sends every finished round to the archive page, which
 * decides `served` vs `results-pending` on whether a pack pointer resolves. The
 * reason is that the probe is an EDITION-DOC read (`handlers/calendar.rs`)
 * while the pointer lives on the refs overlay that stamps
 * `Tournament.bulk_pack_r2_url` (`handlers/tournament.rs` comment D5): pack
 * objects + refs can be published without a namespace bake, so `isPublished`
 * stays false over a round whose games are fully served. This projection stays
 * because the app mirrors it (`CatalogProjections.roundRenderSource`) and the
 * hub still labels rounds with it.
 */
export const calendarRenderSource = (entry: CalendarEntry): RoundRenderSource =>
  roundRenderSource(entry.state, entry.isPublished);

export interface RoundPresentation {
  /** The event name, from the calendar entry's title with the round part stripped. */
  eyebrow: string | null;
  title: string;
  pill: RoundPill;
  /** True when the pill is live but the upstream feed has gone quiet. */
  pillStale: boolean;
  /** AT MOST ONE, by the app's precedence. */
  notice: string | null;
  /** Upcoming only. */
  startLine: string | null;
}

export interface RoundPresentationInput {
  roundId: string;
  streamStatus: StreamStatus;
  boards: readonly LiveGame[];
  /** The matching `ListBroadcasts` entry, or null when the calendar does not know it. */
  calendar: CalendarEntry | null;
  upstreamStale: boolean;
  nowMs: number;
  /** Injectable only so tests can pin a viewer, like `fixturesHeading`. */
  timeZone?: string;
  locale?: string;
}

/**
 * "Team A v Team B", read off the boards rather than off the round's label.
 *
 * Moved here from `pages/round.ts` so the whole title ladder has one home. The
 * app never derives a pairing (`HomeSchedulePresentation.kt:14-20`) because
 * inventing one fabricates data; the web can, because GCL publishes the team in
 * `[WhiteTeam]` and it arrives as `PlayerCard.federation` (README § team names).
 */
export function roundTitle(games: readonly LiveGame[]): string {
  const teams: string[] = [];
  for (const g of games) {
    for (const t of [g.white.federation.trim(), g.black.federation.trim()]) {
      if (t && !teams.some((x) => x.toLowerCase() === t.toLowerCase())) teams.push(t);
    }
  }
  return teams.length === 2 ? `${teams[0]} v ${teams[1]}` : "";
}

/** A served name is usable unless it is blank or the bare word "Round" (`LiveRoundRouter.roundName():52`). */
function servedName(name: string | undefined): string | null {
  const trimmed = (name ?? "").trim();
  if (trimmed.length === 0 || trimmed.toLowerCase() === "round") return null;
  return trimmed;
}

/** Regex-literal characters, escaped for a `u`-mode pattern (where `\-` outside a class is illegal). */
const escapeRegExp = (s: string): string => s.replace(/[\\^$.*+?()[\]{}|]/gu, "\\$&");

/**
 * The event name out of `"Event — Round 11"` (`catalog.proto:633-634`): strip
 * the trailing `" — <round_name>"` when the wire actually pairs them, and leave
 * a title that never carried the suffix alone.
 *
 * The match is case-insensitive and the CUT COMES FROM THE MATCH, never from a
 * folded length. The previous shape tested `title.toLowerCase().endsWith(...)`
 * and then sliced `title.length - round.length` off the ORIGINAL string, which
 * assumes lowercasing preserves UTF-16 length. It does not: `"İ".toLowerCase()`
 * is two units (`i` + combining dot). A title carrying the folded spelling and
 * a `roundName` carrying the dotted capital therefore matched at one length and
 * were cut at another, leaving a stray fragment of the round name glued to the
 * event ("Cup — i"). Anchoring the escaped round name with `iu` cannot go wrong
 * that way: whatever matched is exactly what is removed.
 */
export function eventName(entry: CalendarEntry | null): string | null {
  const title = (entry?.entry.title ?? "").trim();
  if (title.length === 0) return null;
  const round = (entry?.entry.roundName ?? "").trim();
  if (round.length > 0) {
    const found = new RegExp(`${escapeRegExp(round)}$`, "iu").exec(title);
    if (found !== null) {
      const head = title.slice(0, found.index).replace(/[\s—–-]+$/u, "").trim();
      if (head.length > 0) return head;
    }
  }
  return title;
}

/**
 * `"7h 12m"` / `"42m"` / `"Less than a minute"` — the KMP relative formatter
 * `DailyProgressStore.countdownToNextLocalDay():276-285`, mirrored so the round
 * page counts down in the app's words. Only called inside SOON_WINDOW_MS.
 */
export function countdownText(remainingMs: number): string {
  if (remainingMs < 60_000) return "Less than a minute";
  const minutes = Math.floor(remainingMs / 60_000);
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `in ${hours}h ${minutes % 60}m` : `in ${minutes}m`;
}

/**
 * `"3:12:05"`, `"12:05"`, or `"1d 2:01:00"` past a day: the upcoming round's BIG
 * clock (`ui/waiting-room.ts`), repainted every second by the round page's tick.
 *
 * A second presentation of the same remaining time, kept beside
 * {@link countdownText} on purpose: that one is the app's relative words inside
 * a sentence ("Starts 11:15 · in 2h 15m"), minute-grained, and only inside
 * {@link SOON_WINDOW_MS}; this one is a ticking clock face at any distance. The
 * digits are `formatClock`'s — the site's one h:mm:ss owner, the player clocks'
 * — so only the day split is new here. Never negative: a round past its hour is
 * {@link START_SOON}, said by {@link startLine}, never a clock below zero.
 */
export function clockCountdown(remainingMs: number): string {
  const total = Math.max(0, Math.floor(remainingMs / 1000));
  const days = Math.floor(total / 86_400);
  return days > 0 ? `${days}d ${formatClock(total % 86_400, { hours: true })}` : formatClock(total);
}

function startLineFor(input: RoundPresentationInput): string {
  return startLine(input.calendar?.startsAtMs ?? null, input.nowMs, input.timeZone, input.locale);
}

/**
 * When an upcoming round starts, in words — the round page's start line and
 * the Rounds rail's card both say it this way.
 *
 * `countdown: false` is for a surface that is not repainted every second (the
 * rail): a countdown there would freeze at whatever it read on first paint.
 */
export function startLine(
  startsAtMs: number | null,
  nowMs: number,
  timeZone?: string,
  locale?: string,
  opts: { countdown?: boolean } = {},
): string {
  if (startsAtMs === null) return START_AFTER_PREVIOUS;
  const remaining = startsAtMs - nowMs;
  // Past its hour with nothing on the wire: the app refuses to explain and just
  // says "Starts soon" (`TournamentScreen.kt:703`). So do we — the alternative
  // is a negative countdown, which is a claim we cannot support.
  if (remaining <= 0) return START_SOON;
  const when = `Starts ${localWhen(startsAtMs, timeZone, locale)}`;
  return (opts.countdown ?? true) && remaining <= SOON_WINDOW_MS ? `${when} · ${countdownText(remaining)}` : when;
}

/**
 * May the round page open a live subscription at all?
 *
 * A FINISHED round is served by its published pack, never by the live plane:
 * `Subscribe` for it can only ever produce a terminal marker or an error —
 * never boards. The KMP catalog already
 * refuses this exact route — `CatalogProjections.kt:13-16`, "NEVER the live
 * plane: routing this to LIVE made the page open a phantom stream subscription
 * for a finished round" — and #2280 measured what the phantom looks like when
 * ingest is absent: HTTP 200, zero bytes, no trailer, held open.
 *
 * Everything else subscribes, INCLUDING an upcoming round: that stream is what
 * flips the page live on frame one when the round begins. Only an unfillable
 * round is refused. A round the calendar does not know is not refused either —
 * the calendar carries the site's tours, not every round id that can be opened,
 * and `findBroadcast` reports a FAILED lookup as `null` too, so an unreachable
 * calendar subscribes rather than stranding the page.
 */
export function shouldSubscribe(calendar: CalendarEntry | null): boolean {
  return calendar?.state !== "finished";
}

export function deriveRoundPresentation(input: RoundPresentationInput): RoundPresentation {
  const { boards, calendar, streamStatus, upstreamStale } = input;

  // Upcoming is a CALENDAR fact plus the absence of boards; the stream cannot
  // tell us (it is silent either way). One board arriving ends it immediately.
  const upcoming = calendar?.state === "upcoming" && boards.length === 0;
  const pill: RoundPill = upcoming ? "upcoming" : streamStatus === "failed" ? "offline" : streamStatus;
  const pillStale = pill === "live" && upstreamStale;

  return {
    eyebrow: calendar === null ? (input.roundId ? `Round ${input.roundId}` : null) : eventName(calendar),
    title: roundTitle(boards) || servedName(calendar?.entry.roundName) || TITLE_NEUTRAL,
    pill,
    pillStale,
    notice: noticeFor(input, pill),
    startLine: upcoming ? startLineFor(input) : null,
  };
}

/**
 * ONE notice, by `statusNote()`'s precedence. An upcoming round is silent here:
 * the start line already says the true thing, and "Reconnecting…" over a round
 * that simply has not begun is a lie about the backend.
 */
function noticeFor(input: RoundPresentationInput, pill: RoundPill): string | null {
  if (pill === "upcoming") return null;
  if (input.streamStatus === "finished") return NOTICE_ROUND_FINISHED;
  if (input.streamStatus === "failed") return NOTICE_STREAM_LOST;
  if (input.streamStatus === "reconnecting") return NOTICE_RECONNECTING;
  // Before staleness: an empty page needs to be explained before it can be
  // described as delayed, and a round with no boards has no clocks to freeze.
  const onNow = input.calendar !== null && input.calendar.state !== "upcoming";
  if (onNow && input.boards.length === 0) return NOTICE_NO_BOARDS;
  if (input.upstreamStale) return NOTICE_STALE;
  return null;
}
